import { ForwardedRef, forwardRef } from 'react';

import classNames from 'classnames';
import { Directions, Sizes, Alignments, EMPTY_STATE_CLASS } from '@sede-x/shell-ds-react-base';
import { Sentiments } from '@sede-x/shell-ds-fabrics';

import Button from '../Button/Button';
import * as Styled from './EmptyState.styles';
import { IEmptyStateProps } from './EmptyState.types';
import { titleSize, descriptionSize, getVisualPlaceholderType } from './utils/emptyStateUtils';
import ButtonLink from '../ButtonLink/ButtonLink';

const EmptyState = forwardRef(
  (
    {
      className,
      image,
      icon,
      pictogram,
      title,
      description,
      size = Sizes.Medium,
      actions,
      actionsDirection = Directions.Horizontal,
      actionsAlignment,
      actionsFlow,
      ...rest
    }: IEmptyStateProps,
    ref: ForwardedRef<HTMLDivElement>,
  ) => {
    const innerAlignment = actionsAlignment || actionsFlow || Alignments.Center;

    return (
      <Styled.EmptyState ref={ref} size={size} className={classNames(EMPTY_STATE_CLASS, className)} {...rest}>
        {(image || pictogram || icon) && (
          <Styled.VisualPlaceholder
            size={size}
            type={getVisualPlaceholderType(!!image, !!pictogram, !!icon)}
            iconSentiment={icon?.sentiment || Sentiments.Neutral}
          >
            {image || pictogram || icon?.component}
          </Styled.VisualPlaceholder>
        )}

        {(title || description) && (
          <Styled.TextPlaceholder size={size}>
            {title && (
              <Styled.Title size={size} type={titleSize(size)} bold gutter={false}>
                {title}
              </Styled.Title>
            )}
            {typeof description === 'string' ? (
              <Styled.Description size={descriptionSize(size)}>{description}</Styled.Description>
            ) : (
              description
            )}
          </Styled.TextPlaceholder>
        )}

        {actions?.length && (
          <Styled.Actions size={size} actionsDirection={actionsDirection} actionsAlignment={innerAlignment}>
            {actions.map((actionButton) => {
              if ('href' in actionButton) {
                return (
                  <ButtonLink key={actionButton.label} size={size} href={actionButton.href} {...actionButton.props}>
                    {actionButton.label}
                  </ButtonLink>
                );
              }

              if ('action' in actionButton) {
                return (
                  <Button key={actionButton.label} size={size} onClick={actionButton.action} {...actionButton.props}>
                    {actionButton.label}
                  </Button>
                );
              }

              return null;
            })}
          </Styled.Actions>
        )}
      </Styled.EmptyState>
    );
  },
);

export default EmptyState;
